import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useGoogleLogin } from '@react-oauth/google'
import { useAuthStore } from '../stores/authStore'
import { getHealthCheckUrls } from '../config/apiBase'
import './Login.css'

const GOOGLE_ENABLED = !!(import.meta.env.VITE_GOOGLE_CLIENT_ID)

export default function Login() {
  const navigate = useNavigate()
  const { login, loginWithGoogle, user } = useAuthStore()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [serverOk, setServerOk] = useState<'check' | 'yes' | 'no'>('check')
  const [healthCheckKey, setHealthCheckKey] = useState(0)

  useEffect(() => {
    if (user) navigate('/')
  }, [user, navigate])

  useEffect(() => {
    let cancelled = false
    const tryHealth = async () => {
      setServerOk('check')
      for (const url of getHealthCheckUrls()) {
        try {
          const ctrl = new AbortController()
          const t = window.setTimeout(() => ctrl.abort(), 8000)
          const r = await fetch(url, { signal: ctrl.signal, cache: 'no-store' })
          window.clearTimeout(t)
          if (!cancelled && r.ok) { setServerOk('yes'); return }
        } catch { /* try next */ }
      }
      if (!cancelled) setServerOk('no')
    }
    tryHealth()
    return () => { cancelled = true }
  }, [healthCheckKey])

  const googleSignIn = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true)
      setError('')
      try {
        await loginWithGoogle(tokenResponse.access_token, 'access_token')
        navigate('/')
      } catch (err: any) {
        setError(err.message || 'Google sign-in failed')
      } finally {
        setGoogleLoading(false)
      }
    },
    onError: () => setError('Google sign-in was cancelled or failed'),
    flow: 'implicit',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login(username, password)
      navigate('/')
    } catch (err: any) {
      const msg = err?.message || 'Login failed'
      const isNetwork = msg.includes('Network') || msg.includes('ECONNREFUSED') || err?.code === 'ERR_NETWORK'
      setError(isNetwork ? 'Cannot reach server. Make sure the backend is running on port 8000.' : msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>Nexus</h1>
        <h2>Sign In</h2>

        {serverOk === 'check' && <p className="login-server-hint">Checking server connection…</p>}
        {serverOk === 'yes'   && <p className="login-server-ok">Server online — ready to sign in</p>}
        {serverOk === 'no'    && (
          <div className="login-server-bad">
            <p>Nexus server not detected on port 8000. Run <code>./start_backend.sh</code></p>
            <button type="button" className="login-retry-health" onClick={() => setHealthCheckKey(k => k + 1)}>
              Retry
            </button>
          </div>
        )}

        {/* Google button — only shown when Client ID is configured */}
        {GOOGLE_ENABLED && (
          <>
            <button
              type="button"
              className="login-google-btn"
              onClick={() => googleSignIn()}
              disabled={loading || googleLoading}
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              {googleLoading ? 'Signing in…' : 'Continue with Google'}
            </button>
            <div className="login-divider"><span>or sign in with username</span></div>
          </>
        )}

        <form onSubmit={handleSubmit}>
          <input type="text" placeholder="Username" value={username}
            onChange={e => setUsername(e.target.value)} required autoComplete="username" />
          <input type="password" placeholder="Password" value={password}
            onChange={e => setPassword(e.target.value)} required autoComplete="current-password" />
          {error && <div className="error">{error}</div>}
          <button type="submit" disabled={loading || googleLoading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
          <div className="login-footer-hint">
            <p>Default admin: <strong>admin / admin</strong></p>
          </div>
        </form>

        <p className="login-register-link">
          Don't have an account? <Link to="/register">Create one</Link>
        </p>
      </div>
    </div>
  )
}
